import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";

import {
  FaArrowLeft,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUser,
  FaRulerCombined,
  FaLayerGroup,
  FaImages,
  FaVideo,
  FaBuilding,
  FaCheckCircle,
  FaExpand,
  FaTimes,
} from "react-icons/fa";

import { db } from "../../firebase/firebase";

import "./ProjectDetails.css";

const ProjectDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedImage, setSelectedImage] = useState(null);

  /* =====================================================
     LOAD PROJECT
  ===================================================== */

  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);
        setError("");

        console.log("Loading PUBLIC project slug:", slug);

        if (!slug) {
          throw new Error("Project slug is missing.");
        }

        let projectData = null;

        /* =================================================
           FIRST TRY: FIND BY SLUG
        ================================================= */

        const projectsRef = collection(db, "projects");

        const slugQuery = query(projectsRef, where("slug", "==", slug));

        const slugSnapshot = await getDocs(slugQuery);

        if (!slugSnapshot.empty) {
          const projectDoc = slugSnapshot.docs[0];

          projectData = {
            id: projectDoc.id,
            ...projectDoc.data(),
          };
        }

        /* =================================================
           FALLBACK: IF URL PARAMETER IS FIREBASE ID
        ================================================= */

        if (!projectData) {
          try {
            const projectRef = doc(db, "projects", slug);

            const projectSnapshot = await getDoc(projectRef);

            if (projectSnapshot.exists()) {
              projectData = {
                id: projectSnapshot.id,
                ...projectSnapshot.data(),
              };
            }
          } catch (idError) {
            console.log("Firebase ID fallback failed:", idError);
          }
        }

        /* =================================================
           PROJECT NOT FOUND
        ================================================= */

        if (!projectData) {
          throw new Error("Project not found in Firebase.");
        }

        console.log("PUBLIC PROJECT LOADED:", projectData);

        setProject(projectData);
      } catch (err) {
        console.error("PUBLIC PROJECT DETAILS ERROR:", err);

        setError(err.message || "Unable to load this project.");
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [slug]);

  /* =====================================================
     FORMAT DATE
  ===================================================== */

  const formatDate = (date) => {
    if (!date) {
      return "Not specified";
    }

    try {
      /*
       * Firebase Timestamp
       */
      if (typeof date === "object" && typeof date.toDate === "function") {
        return date.toDate().toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      }

      const parsedDate = new Date(date);

      if (isNaN(parsedDate.getTime())) {
        return date;
      }

      return parsedDate.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return "Not specified";
    }
  };

  /* =====================================================
     GALLERY NORMALIZER
  ===================================================== */

  const getGalleryImages = () => {
    if (!project || !Array.isArray(project.galleryImages)) {
      return [];
    }

    return project.galleryImages
      .map((image) => {
        if (typeof image === "string") {
          return image;
        }

        if (image && typeof image === "object") {
          return image.url || image.secure_url || image.src || "";
        }

        return "";
      })
      .filter(Boolean);
  };

  /* =====================================================
     VIDEO
  ===================================================== */

  const getVideoUrl = () => {
    if (!project) {
      return "";
    }

    if (typeof project.video === "string") {
      return project.video;
    }

    if (project.video && typeof project.video === "object") {
      return project.video.url || project.video.secure_url || "";
    }

    return project.videoUrl || project.cloudinaryVideo || "";
  };

  /* =====================================================
     COVER IMAGE
  ===================================================== */

  const coverImage =
    project?.coverImage || project?.coverImageUrl || project?.image || "";

  const galleryImages = getGalleryImages();

  const video = getVideoUrl();

  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {
    return (
      <main className="public-project-details-page">
        <div className="public-details-loading">
          <div className="public-details-spinner"></div>

          <h2>Loading Project...</h2>

          <p>Please wait while we load the project details.</p>
        </div>
      </main>
    );
  }

  /* =====================================================
     ERROR
  ===================================================== */

  if (error) {
    return (
      <main className="public-project-details-page">
        <div className="public-details-error">
          <div className="public-details-error-icon">!</div>

          <h2>Unable to Load Project</h2>

          <p>{error}</p>

          <button
            className="public-details-back-button"
            onClick={() => navigate("/projects")}
          >
            <FaArrowLeft />
            Back to Projects
          </button>
        </div>
      </main>
    );
  }

  /* =====================================================
     PROJECT NOT FOUND
  ===================================================== */

  if (!project) {
    return (
      <main className="public-project-details-page">
        <div className="public-details-error">
          <div className="public-details-error-icon">!</div>

          <h2>Project Not Found</h2>

          <p>This project could not be found.</p>

          <button
            className="public-details-back-button"
            onClick={() => navigate("/projects")}
          >
            <FaArrowLeft />
            Back to Projects
          </button>
        </div>
      </main>
    );
  }

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <main className="public-project-details-page">
      {/* =================================================
          HERO
      ================================================= */}

      <section className="public-project-details-hero">
        <div className="public-details-hero-overlay"></div>

        {coverImage && (
          <img
            className="public-details-hero-background"
            src={coverImage}
            alt=""
          />
        )}

        <div className="public-details-hero-content">
          <button
            className="public-details-back-link"
            onClick={() => navigate("/projects")}
          >
            <FaArrowLeft />
            Back to Projects
          </button>

          <div className="public-details-hero-info">
            {project.sport && (
              <span className="public-details-sport">{project.sport}</span>
            )}

            <h1>{project.title || "Sports Project"}</h1>

            {project.shortDescription && (
              <p className="public-details-short-description">
                {project.shortDescription}
              </p>
            )}

            {(project.location || project.state) && (
              <div className="public-details-location">
                <FaMapMarkerAlt />

                <span>
                  {project.location}

                  {project.location && project.state ? ", " : ""}

                  {project.state}
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* =================================================
          MAIN CONTENT
      ================================================= */}

      <section className="public-details-section">
        <div className="public-details-container">
          <div className="public-details-layout">
            {/* =================================================
                MAIN COLUMN
            ================================================= */}

            <div className="public-details-main">
              {/* =================================================
                  DESCRIPTION
              ================================================= */}

              <section className="public-details-card">
                <div className="public-details-section-heading">
                  <span>ABOUT THE PROJECT</span>

                  <h2>Project Description</h2>
                </div>

                <div className="public-details-description">
                  {project.description ? (
                    <p>{project.description}</p>
                  ) : project.shortDescription ? (
                    <p>{project.shortDescription}</p>
                  ) : (
                    <p className="public-details-muted">
                      No project description available.
                    </p>
                  )}
                </div>
              </section>

              {/* =================================================
                  GALLERY
              ================================================= */}

              <section className="public-details-card">
                <div className="public-details-section-heading gallery-heading">
                  <div>
                    <span>PROJECT GALLERY</span>

                    <h2>Completed Work</h2>
                  </div>

                  <div className="public-gallery-count">
                    <FaImages />
                    {galleryImages.length}{" "}
                    {galleryImages.length === 1 ? "Image" : "Images"}
                  </div>
                </div>

                {galleryImages.length > 0 ? (
                  <div className="public-details-gallery">
                    {galleryImages.map((image, index) => (
                      <button
                        type="button"
                        className="public-gallery-item"
                        key={`${image}-${index}`}
                        onClick={() => setSelectedImage(image)}
                      >
                        <img
                          src={image}
                          alt={`${project.title || "Project"} ${index + 1}`}
                        />

                        <span className="public-gallery-overlay">
                          <FaExpand />
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="public-details-empty-media">
                    <FaImages />

                    <p>No gallery images available for this project.</p>
                  </div>
                )}
              </section>

              {/* =================================================
                  VIDEO
              ================================================= */}

              <section className="public-details-card">
                <div className="public-details-section-heading">
                  <span>PROJECT VIDEO</span>

                  <h2>Construction & Surface Work</h2>
                </div>

                {video ? (
                  <div className="public-details-video">
                    <video
                      src={video}
                      controls
                      preload="metadata"
                      playsInline
                    />
                  </div>
                ) : (
                  <div className="public-details-empty-media">
                    <FaVideo />

                    <p>No project video available.</p>
                  </div>
                )}
              </section>
            </div>

            {/* =================================================
                SIDEBAR
            ================================================= */}

            <aside className="public-details-sidebar">
              {/* =================================================
                  PROJECT INFORMATION
              ================================================= */}

              <div className="public-details-info-card">
                <div className="public-details-info-heading">
                  <span>PROJECT DETAILS</span>

                  <h3>Project Information</h3>
                </div>

                <div className="public-details-info-list">
                  {/* SPORT */}

                  <div className="public-details-info-item">
                    <div className="public-details-info-icon">
                      <FaBuilding />
                    </div>

                    <div>
                      <span>Sport</span>

                      <strong>{project.sport || "Not specified"}</strong>
                    </div>
                  </div>

                  {/* SURFACE */}

                  <div className="public-details-info-item">
                    <div className="public-details-info-icon">
                      <FaLayerGroup />
                    </div>

                    <div>
                      <span>Surface</span>

                      <strong>{project.surface || "Not specified"}</strong>
                    </div>
                  </div>

                  {/* CLIENT */}

                  <div className="public-details-info-item">
                    <div className="public-details-info-icon">
                      <FaUser />
                    </div>

                    <div>
                      <span>Client</span>

                      <strong>{project.client || "Not specified"}</strong>
                    </div>
                  </div>

                  {/* LOCATION */}

                  <div className="public-details-info-item">
                    <div className="public-details-info-icon">
                      <FaMapMarkerAlt />
                    </div>

                    <div>
                      <span>Location</span>

                      <strong>{project.location || "Not specified"}</strong>
                    </div>
                  </div>

                  {/* COMPLETION DATE */}

                  <div className="public-details-info-item">
                    <div className="public-details-info-icon">
                      <FaCalendarAlt />
                    </div>

                    <div>
                      <span>Completion Date</span>

                      <strong>{formatDate(project.completionDate)}</strong>
                    </div>
                  </div>

                  {/* AREA */}

                  <div className="public-details-info-item">
                    <div className="public-details-info-icon">
                      <FaRulerCombined />
                    </div>

                    <div>
                      <span>Total Area</span>

                      <strong>
                        {project.area
                          ? `${project.area} sq.ft`
                          : "Not specified"}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* =================================================
                  DIMENSIONS
              ================================================= */}

              <div className="public-details-info-card">
                <div className="public-details-info-heading">
                  <span>MEASUREMENTS</span>

                  <h3>Dimensions</h3>
                </div>

                <div className="public-dimensions-grid">
                  <div>
                    <span>Length</span>

                    <strong>
                      {project.courtLength ? `${project.courtLength} ft` : "—"}
                    </strong>
                  </div>

                  <div>
                    <span>Width</span>

                    <strong>
                      {project.courtWidth ? `${project.courtWidth} ft` : "—"}
                    </strong>
                  </div>

                  <div>
                    <span>Area</span>

                    <strong>
                      {project.area ? `${project.area} sq.ft` : "—"}
                    </strong>
                  </div>
                </div>
              </div>

              {/* =================================================
                  FEATURED
              ================================================= */}

              {project.featured && (
                <div className="public-featured-card">
                  <FaCheckCircle />

                  <div>
                    <strong>Featured Project</strong>

                    <span>One of our highlighted sports surface projects.</span>
                  </div>
                </div>
              )}

              {/* =================================================
                  CTA
              ================================================= */}

              <div className="public-details-quote-card">
                <span>START YOUR PROJECT</span>

                <h3>Need a Similar Sports Facility?</h3>

                <p>Talk to our team about your next sports surface project.</p>

                <button type="button" onClick={() => navigate("/contact")}>
                  Get a Free Quote
                  <span>→</span>
                </button>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* =================================================
          IMAGE LIGHTBOX
      ================================================= */}

      {selectedImage && (
        <div
          className="public-image-lightbox"
          onClick={() => setSelectedImage(null)}
        >
          <button
            type="button"
            className="public-lightbox-close"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedImage(null);
            }}
          >
            <FaTimes />
          </button>

          <img
            src={selectedImage}
            alt={project.title || "Project gallery"}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </main>
  );
};

export default ProjectDetails;
